import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n";
import { Reveal, MaskedLine, Label } from "../components/Motion";
import DeptMarquee from "../components/DeptMarquee";
import { usePageMeta } from "../lib/seo";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwbWFjaGluZXJ5JTIwcHJlY2lzaW9uJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3ODcwMzcyMzZ8MA&ixlib=rb-4.1.0&q=85",
  design: "https://images.pexels.com/photos/19895882/pexels-photo-19895882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  fabrication: "/images/dept-fabrication.webp",
  assembly: "/images/dept-assembly.webp",
  milling: "/images/dept-milling.webp",
  turning: "/images/dept-turning.webp",
  cnc: "/images/dept-cnc.webp",
  customPanel: "/images/service-custom.webp",
  overhaulPanel: "/images/service-overhaul.webp",
};

const DEPT_IMAGES = [IMAGES.design, IMAGES.fabrication, IMAGES.assembly, IMAGES.milling, IMAGES.turning, IMAGES.cnc];
const DEPT_SPANS = ["md:col-span-2", "md:col-span-4", "md:col-span-2", "md:col-span-4", "md:col-span-3", "md:col-span-3"];

export default function Home() {
  const { t } = useLanguage();
  usePageMeta(t("meta.home.t"), t("meta.home.d"));

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const depts = t("departments.items");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img
            src={IMAGES.hero}
            alt="Precision industrial machinery"
            className="h-full w-full object-cover"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40 md:px-12">
          <MaskedLine delay={0.15}>
            <p data-testid="hero-kicker" className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow md:text-sm">
              <span className="inline-block h-px w-12 bg-brand-yellow" />
              GTECH SRL — {t("hero.kicker")}
            </p>
          </MaskedLine>

          <h1 data-testid="hero-title" className="font-display text-[clamp(3rem,10vw,9rem)] font-black leading-[0.92] tracking-tighter">
            <MaskedLine delay={0.3}>{t("hero.l1")}</MaskedLine>
            <MaskedLine delay={0.42}>{t("hero.l2")}</MaskedLine>
            <MaskedLine delay={0.54}>
              <span className="text-outline">{t("hero.l3a")}</span>{" "}
              <span className="text-brand-yellow">{t("hero.l3b")}</span>
            </MaskedLine>
          </h1>

          <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <Reveal delay={0.75}>
              <p data-testid="hero-sub" className="max-w-md text-base leading-relaxed text-neutral-300 md:text-lg">
                {t("hero.sub")}
              </p>
            </Reveal>
            <Reveal delay={0.9} className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group flex items-center gap-2 bg-brand-yellow px-7 py-4 text-sm font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
              >
                {t("hero.cta1")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-brand-yellow hover:text-brand-yellow"
              >
                {t("hero.cta2")}
              </Link>
            </Reveal>
          </div>
        </motion.div>

        <div data-testid="hero-scroll-cue" className="absolute bottom-8 right-6 z-10 hidden flex-col items-center gap-3 md:right-12 md:flex">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50 [writing-mode:vertical-lr]">{t("hero.scroll")}</span>
          <span className="block h-16 w-px animate-scroll-cue bg-brand-yellow" />
        </div>
      </section>

      <DeptMarquee />

      {/* INTRO */}
      <section data-testid="intro-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <Label>{t("intro.label")}</Label>
          </Reveal>
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <h2 data-testid="intro-title" className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                {t("intro.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-400 md:text-lg">{t("intro.body")}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/about"
                data-testid="intro-cta"
                className="group mt-10 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-yellow"
              >
                {t("intro.cta")}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DEPARTMENTS BENTO */}
      <section data-testid="departments-section" className="border-t border-white/10 bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal><Label>{t("departments.label")}</Label></Reveal>
              <Reveal delay={0.1}>
                <h2 data-testid="departments-title" className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
                  {t("departments.title")}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <p className="max-w-sm text-base text-neutral-400">{t("departments.sub")}</p>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-4 md:grid-cols-6">
            {depts.map((d, i) => (
              <Reveal key={i} delay={0.08 * i} className={DEPT_SPANS[i]}>
                <Link
                  to="/services"
                  data-testid={`dept-card-${i}`}
                  className="group relative block h-full overflow-hidden border border-brand-line bg-brand-surface transition-colors duration-500 hover:border-brand-yellow/60"
                >
                  <div className={`relative overflow-hidden ${i === 1 ? "h-72 md:h-96" : "h-52 md:h-60"}`}>
                    <img
                      src={DEPT_IMAGES[i]}
                      alt={d.name}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-transparent to-transparent" />
                    <span className="absolute right-5 top-4 font-display text-5xl font-black text-white/15 transition-colors duration-500 group-hover:text-brand-yellow/40">
                      0{i + 1}
                    </span>
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="font-display text-xl font-bold tracking-tight md:text-2xl">{d.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-12">
            <Link
              to="/services"
              data-testid="departments-cta"
              className="group inline-flex items-center gap-2 bg-brand-yellow px-7 py-4 text-sm font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
            >
              {t("departments.cta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES SPLIT */}
      <section data-testid="services-home-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <Reveal><Label>{t("servicesHome.label")}</Label></Reveal>

        <div className="mt-16 grid gap-4 md:grid-cols-2">
          {[
            { img: IMAGES.customPanel, data: t("servicesHome.custom"), id: "custom" },
            { img: IMAGES.overhaulPanel, data: t("servicesHome.overhaul"), id: "overhaul" },
          ].map((s, i) => (
            <Reveal key={s.id} delay={0.1 * i}>
              <div
                data-testid={`service-panel-${s.id}`}
                className="group relative flex min-h-[480px] flex-col justify-end overflow-hidden border border-brand-line transition-colors duration-500 hover:border-brand-yellow/60"
              >
                <img
                  src={s.img}
                  alt={s.data.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover brightness-[0.35] transition-all duration-700 group-hover:scale-105 group-hover:brightness-[0.45]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative p-8 md:p-12">
                  <h3 className="font-display text-2xl font-bold tracking-tight md:text-4xl">{s.data.title}</h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-300 md:text-base">{s.data.desc}</p>
                  <Link
                    to="/contact"
                    data-testid={`service-panel-cta-${s.id}`}
                    className="group/btn mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-yellow"
                  >
                    {s.data.cta}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRIVATE CUSTOMERS */}
      <section data-testid="private-section" className="border-t border-white/10 bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal><Label>{t("privateSection.label")}</Label></Reveal>
              <Reveal delay={0.1}>
                <h2 data-testid="private-title" className="mt-6 max-w-2xl font-display text-4xl font-bold tracking-tight md:text-6xl">
                  {t("privateSection.title")}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <p className="max-w-sm text-base leading-relaxed text-neutral-400">{t("privateSection.body")}</p>
            </Reveal>
          </div>
          <div className="mt-16 grid gap-4 md:grid-cols-3">
            {t("privateSection.items").map((item, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <div data-testid={`private-card-${i}`} className="group relative overflow-hidden border border-brand-line">
                  <img
                    src={item.img}
                    alt={item.name}
                    loading="lazy"
                    className="h-80 w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <p className="absolute bottom-5 left-5 font-display text-xl font-bold tracking-tight text-white">{item.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <Link
              to="/contact"
              data-testid="private-cta"
              className="group inline-flex items-center gap-2 bg-brand-yellow px-7 py-4 text-sm font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
            >
              {t("privateSection.cta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="cta-section" className="border-t border-white/10 bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-28 text-center md:px-12 md:py-40">
          <Reveal>
            <h2 data-testid="cta-title" className="font-display text-[clamp(2.5rem,8vw,7rem)] font-black leading-none tracking-tighter">
              <span className="text-outline-yellow">{t("ctaSection.title").split(" ")[0]}</span>{" "}
              <span className="text-brand-yellow">{t("ctaSection.title").split(" ").slice(1).join(" ")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-8 max-w-xl text-base text-neutral-400 md:text-lg">{t("ctaSection.sub")}</p>
          </Reveal>
          <Reveal delay={0.25} className="mt-12 flex flex-col items-center gap-6">
            <Link
              to="/contact"
              data-testid="cta-section-button"
              className="group inline-flex items-center gap-3 bg-brand-yellow px-10 py-5 font-display text-base font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
            >
              {t("ctaSection.btn")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <p className="text-sm text-neutral-500">
              {t("ctaSection.or")}{" "}
              <a data-testid="cta-email-link" href="mailto:info@gtechitalia.it" className="font-bold text-white transition-colors hover:text-brand-yellow">
                info@gtechitalia.it
              </a>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
